/**
 * App rail primitive — started as a Tailwind v3 port of shadcn/ui's
 * `sidebar.tsx`, now trimmed to an always-narrow rail. Deliberate departures
 * from upstream:
 *
 * - **No expand/collapse, no hover-to-peek.** The desktop rail is one fixed
 *   width (`--sidebar-width`, 72px), each icon above its label (R4, Kapil 2 Oct).
 * - **No Cmd/Ctrl+B shortcut.** Upstream binds it globally; here it would steal
 *   Bold from the TipTap editor.
 * - **No cookie, no persisted state.** The only state is the mobile sheet.
 * - **No `asChild`.** `@radix-ui/react-slot` is not a direct dependency, so menu
 *   buttons always render a `<button>`; callers navigate in `onClick`.
 * - **No `sidebar-accent` colours.** Our `--sidebar-accent` is near-black and
 *   means something else. Menu buttons carry the nav spec (soft dark wash)
 *   directly.
 * - **`SidebarInset` is a `<div>`, not `<main>`** — several pages render their
 *   own `<main>`, and landmarks must not nest.
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { PanelLeft } from "lucide-react"
import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet"
import { TooltipProvider } from "@/components/ui/tooltip"

const SIDEBAR_WIDTH = "4.5rem"
const SIDEBAR_WIDTH_MOBILE = "16rem"

type SidebarCssVars = React.CSSProperties & Record<`--${string}`, string>

// ===================
// Context
// ===================

interface SidebarContextValue {
  isMobile: boolean
  openMobile: boolean
  setOpenMobile: (open: boolean) => void
  /** Opens or closes the mobile sheet. No-op on desktop: the rail is always shown. */
  toggleSidebar: () => void
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null)

function useSidebar(): SidebarContextValue {
  const context = React.useContext(SidebarContext)
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.")
  }
  return context
}

// ===================
// Provider
// ===================

const SidebarProvider = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, style, children, ...props }, ref) => {
    const isMobile = useIsMobile()
    const [openMobile, setOpenMobile] = React.useState(false)

    const toggleSidebar = React.useCallback(() => {
      if (isMobile) setOpenMobile((current) => !current)
    }, [isMobile])

    const contextValue = React.useMemo<SidebarContextValue>(
      () => ({ isMobile, openMobile, setOpenMobile, toggleSidebar }),
      [isMobile, openMobile, toggleSidebar]
    )

    const cssVars: SidebarCssVars = {
      "--sidebar-width": SIDEBAR_WIDTH,
      ...style,
    }

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            ref={ref}
            style={cssVars}
            className={cn("group/sidebar-wrapper flex min-h-svh w-full", className)}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    )
  }
)
SidebarProvider.displayName = "SidebarProvider"

// ===================
// Sidebar
// ===================

const Sidebar = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const { isMobile, openMobile, setOpenMobile } = useSidebar()

    if (isMobile) {
      const mobileVars: SidebarCssVars = { "--sidebar-width": SIDEBAR_WIDTH_MOBILE }
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            side="left"
            hideClose
            className="w-[--sidebar-width] bg-sidebar p-0 text-sidebar-foreground"
            style={mobileVars}
          >
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      )
    }

    // A plain flex child: the shell is `h-screen`, so the rail fills the height
    // and the content pane takes the rest. Its own border-r is the only seam.
    return (
      <nav
        ref={ref}
        aria-label="Main"
        data-sidebar="sidebar"
        data-variant="rail"
        className={cn(
          "hidden h-svh w-[--sidebar-width] shrink-0 flex-col border-r border-border bg-sidebar text-sidebar-foreground md:flex",
          className
        )}
        {...props}
      >
        {children}
      </nav>
    )
  }
)
Sidebar.displayName = "Sidebar"

// ===================
// Trigger (mobile menu button)
// ===================

const SidebarTrigger = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, onClick, children, ...props }, ref) => {
    const { toggleSidebar } = useSidebar()
    return (
      <Button
        ref={ref}
        data-sidebar="trigger"
        variant="ghost"
        size="icon"
        className={cn("h-7 w-7", className)}
        onClick={(event) => {
          onClick?.(event)
          toggleSidebar()
        }}
        {...props}
      >
        {children ?? <PanelLeft className="h-4 w-4" />}
      </Button>
    )
  }
)
SidebarTrigger.displayName = "SidebarTrigger"

// ===================
// Layout slots
// ===================

const SidebarInset = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("relative flex min-w-0 flex-1 flex-col bg-background", className)}
      {...props}
    />
  )
)
SidebarInset.displayName = "SidebarInset"

const SidebarHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} data-sidebar="header" className={cn("flex flex-col gap-2 p-2", className)} {...props} />
  )
)
SidebarHeader.displayName = "SidebarHeader"

const SidebarFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} data-sidebar="footer" className={cn("flex flex-col gap-2 p-2", className)} {...props} />
  )
)
SidebarFooter.displayName = "SidebarFooter"

const SidebarContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn("flex min-h-0 flex-1 flex-col gap-2 overflow-auto", className)}
      {...props}
    />
  )
)
SidebarContent.displayName = "SidebarContent"

// ===================
// Menu
// ===================

const SidebarMenu = React.forwardRef<HTMLUListElement, React.HTMLAttributes<HTMLUListElement>>(
  ({ className, ...props }, ref) => (
    <ul
      ref={ref}
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      {...props}
    />
  )
)
SidebarMenu.displayName = "SidebarMenu"

const SidebarMenuItem = React.forwardRef<HTMLLIElement, React.LiHTMLAttributes<HTMLLIElement>>(
  ({ className, ...props }, ref) => (
    <li ref={ref} data-sidebar="menu-item" className={cn("group/menu-item relative", className)} {...props} />
  )
)
SidebarMenuItem.displayName = "SidebarMenuItem"

// Design-system rail spec: soft dark wash for hover (/[0.05]) and active (/[0.07]),
// near-black + font-medium when active. `rail` stacks a 20px icon over an 11px
// label; `row` is the mobile sheet's full-width labelled row.
const sidebarMenuButtonVariants = cva(
  [
    "peer/menu-button flex w-full items-center overflow-hidden rounded-[6px]",
    "text-sidebar-foreground outline-none transition-colors",
    "hover:bg-foreground/[0.05] hover:text-foreground",
    "focus-visible:ring-2 focus-visible:ring-ring",
    "disabled:pointer-events-none disabled:opacity-50",
    "data-[active=true]:bg-foreground/[0.07] data-[active=true]:font-medium data-[active=true]:text-foreground",
    "[&>svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      layout: {
        rail: "flex-col justify-center gap-1 px-0.5 py-1.5 text-center text-[11px] leading-tight [&>svg]:h-5 [&>svg]:w-5",
        row: "h-8 gap-2 px-3 text-left text-sm [&>span:last-child]:truncate [&>svg]:h-4 [&>svg]:w-4",
      },
    },
    defaultVariants: { layout: "rail" },
  }
)

interface SidebarMenuButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof sidebarMenuButtonVariants> {
  isActive?: boolean
}

const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  ({ isActive = false, layout, className, type = "button", ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      data-sidebar="menu-button"
      data-layout={layout ?? "rail"}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ layout }), className)}
      {...props}
    />
  )
)
SidebarMenuButton.displayName = "SidebarMenuButton"

// The count number is the azure accent (Kapil, 2026-10-16): in grey it read as
// part of the icon. `--link`, not `--brand`: `--brand` is 4.32:1 on the pill's
// `bg-muted`, under the 4.5:1 text floor; `--link` is 5.26:1 there.
const sidebarMenuBadgeVariants = cva(
  "pointer-events-none absolute flex select-none items-center justify-center tabular-nums",
  {
    variants: {
      layout: {
        // A small pill over the icon's top-right corner.
        rail: "right-2 top-0.5 h-4 min-w-4 rounded-full bg-muted px-1 text-[10px] font-medium leading-none text-[hsl(var(--link))]",
        // The sheet row: plain caption-weight number at the row's right edge.
        row: "right-2 top-1.5 h-5 min-w-5 px-1 text-meta text-[hsl(var(--link))]",
      },
    },
    defaultVariants: { layout: "rail" },
  }
)

interface SidebarMenuBadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof sidebarMenuBadgeVariants> {}

const SidebarMenuBadge = React.forwardRef<HTMLDivElement, SidebarMenuBadgeProps>(
  ({ layout, className, ...props }, ref) => (
    <div
      ref={ref}
      data-sidebar="menu-badge"
      className={cn(sidebarMenuBadgeVariants({ layout }), className)}
      {...props}
    />
  )
)
SidebarMenuBadge.displayName = "SidebarMenuBadge"

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
}
